<script setup>
import HrBanner from '../primitives/HrBanner.vue';

/**
 * Informational banner for neutral messages the user should be aware of.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Communicating a tip, guidance, or non-urgent contextual information
 * @avoidWhen The message requires immediate attention — use HrErrorBanner or HrWarningBanner
 * @related HrErrorBanner
 * @related HrWarningBanner
 * @related HrSuccessBanner
 * @event dismiss Fired when the user closes the banner via the dismiss button.
 */
defineOptions({ name: 'HrInfoBanner' });

defineProps({
  /**
   * Message text. Use the default slot for richer content.
   * @replacesSlot default
   */
  message: { type: String, default: null },
  /** Optional heading displayed above the message. */
  heading: { type: String, default: null },
  /** Material icon name for the leading icon. */
  icon: { type: String, default: 'info' },
  /** Shows a dismiss button. When clicked, the banner hides and emits `dismiss`. */
  dismissible: { type: Boolean, default: false },
  /** Accessible label for the dismiss button; only meaningful when `dismissible` is true. */
  closeLabel: { type: String, default: 'Dismiss banner' },
  /** Removes the border radius — use when the banner spans the full container width. */
  fullBleed: { type: Boolean, default: false },
  /** Switches to a stacked layout that accommodates longer messages and action buttons. */
  multiLine: { type: Boolean, default: false },
});
</script>

<template>
  <hr-banner v-bind="{ ...$attrs, ...$props, colorRole: 'primary', headingLevel: 'h2' }">
    <!-- @slot Message content; renders in place of the `message` prop. -->
    <slot />
    <template #icon>
      <!-- @slot Custom icon; replaces the default info icon. -->
      <slot name="icon" />
    </template>
    <template #action>
      <!-- @slot Action button(s) placed in the banner aside. -->
      <slot name="action" />
    </template>
  </hr-banner>
</template>
